import React, { useState, useEffect } from 'react';
import { createPaymentOrder, verifyPayment } from '../api';
import styles from './PaymentGateway.module.css';

const PaymentGateway = ({ 
  amount, 
  bookingId, 
  patientName, 
  doctorName, 
  onPaymentSuccess, 
  onPaymentFailure, 
  onClose 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedSubOption, setSelectedSubOption] = useState(null);
  const [showSubOptions, setShowSubOptions] = useState(false);

  const paymentMethods = [
    {
      id: 'upi',
      name: 'UPI',
      icon: '💳',
      description: 'Pay using UPI apps like GPay, PhonePe, Paytm',
      color: '#6366f1',
      subOptions: [
        { id: 'gpay', name: 'Google Pay', icon: '📱', color: '#4285f4' },
        { id: 'phonepe', name: 'PhonePe', icon: '📱', color: '#5f259f' },
        { id: 'paytm', name: 'Paytm', icon: '📱', color: '#00b9f1' },
        { id: 'amazonpay', name: 'Amazon Pay', icon: '📱', color: '#ff9900' },
        { id: 'bhim', name: 'BHIM UPI', icon: '📱', color: '#00a651' },
        { id: 'other', name: 'Other UPI Apps', icon: '📱', color: '#6b7280' }
      ]
    },
    {
      id: 'card',
      name: 'Credit/Debit Card',
      icon: '💳',
      description: 'Pay using credit or debit card',
      color: '#059669',
      subOptions: [
        { id: 'visa', name: 'Visa', icon: '💳', color: '#1a1f71' },
        { id: 'mastercard', name: 'Mastercard', icon: '💳', color: '#eb001b' },
        { id: 'amex', name: 'American Express', icon: '💳', color: '#006fcf' },
        { id: 'rupay', name: 'RuPay', icon: '💳', color: '#0f4c81' },
        { id: 'other_card', name: 'Other Cards', icon: '💳', color: '#6b7280' }
      ]
    },
    {
      id: 'netbanking',
      name: 'Net Banking',
      icon: '🏦',
      description: 'Pay using your bank account',
      color: '#dc2626',
      subOptions: [
        { id: 'sbi', name: 'State Bank of India', icon: '🏦', color: '#1e40af' },
        { id: 'hdfc', name: 'HDFC Bank', icon: '🏦', color: '#059669' },
        { id: 'icici', name: 'ICICI Bank', icon: '🏦', color: '#dc2626' },
        { id: 'axis', name: 'Axis Bank', icon: '🏦', color: '#7c3aed' },
        { id: 'kotak', name: 'Kotak Bank', icon: '🏦', color: '#ea580c' },
        { id: 'other_bank', name: 'Other Banks', icon: '🏦', color: '#6b7280' }
      ]
    },
    {
      id: 'wallet',
      name: 'Digital Wallet',
      icon: '👛',
      description: 'Pay using digital wallets',
      color: '#ea580c',
      subOptions: [
        { id: 'paytm_wallet', name: 'Paytm Wallet', icon: '👛', color: '#00b9f1' },
        { id: 'amazon_wallet', name: 'Amazon Pay Wallet', icon: '👛', color: '#ff9900' },
        { id: 'phonepe_wallet', name: 'PhonePe Wallet', icon: '👛', color: '#5f259f' },
        { id: 'mobikwik', name: 'MobiKwik', icon: '👛', color: '#ea580c' },
        { id: 'other_wallet', name: 'Other Wallets', icon: '👛', color: '#6b7280' }
      ]
    },
    {
      id: 'emi',
      name: 'EMI',
      icon: '📅',
      description: 'Pay in easy installments',
      color: '#7c3aed',
      subOptions: [
        { id: '3_months', name: '3 Months EMI', icon: '📅', color: '#059669' },
        { id: '6_months', name: '6 Months EMI', icon: '📅', color: '#7c3aed' },
        { id: '9_months', name: '9 Months EMI', icon: '📅', color: '#dc2626' },
        { id: '12_months', name: '12 Months EMI', icon: '📅', color: '#ea580c' },
        { id: 'other_emi', name: 'Other EMI Options', icon: '📅', color: '#6b7280' }
      ]
    }
  ];

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(window.Razorpay);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(window.Razorpay);
      script.onerror = () => reject(new Error('Failed to load Razorpay'));
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (paymentMethod, subOption) => {
    try {
      setLoading(true);
      setError(null);

      // Create payment order
      const orderResponse = await createPaymentOrder({
        amount: amount,
        bookingId: bookingId,
        currency: 'INR',
        paymentMethod: paymentMethod,
        paymentSubMethod: subOption
      });

      // For testing purposes, simulate a successful payment
      if (orderResponse.orderId && orderResponse.orderId.startsWith('order_')) {
        // This is a mock order, simulate payment success
        console.log('Processing mock payment...');
        setTimeout(async () => {
          try {
            // Simulate payment verification
            const verificationResponse = await verifyPayment({
              razorpayOrderId: orderResponse.orderId,
              razorpayPaymentId: 'pay_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
              razorpaySignature: 'mock_signature_' + Date.now(),
              bookingId: bookingId,
              paymentMethod: paymentMethod,
              paymentSubMethod: subOption
            });

            if (verificationResponse.success) {
              console.log('Mock payment successful!');
              onPaymentSuccess(verificationResponse.booking);
            } else {
              onPaymentFailure('Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            onPaymentFailure(error.message || 'Payment verification failed');
          }
        }, 2000); // Simulate 2 second payment process
      } else {
        // Try to load Razorpay for real payments
        try {
          const Razorpay = await loadRazorpayScript();

          // Configure payment options
          const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_1DP5mmOlF5G5ag',
            amount: orderResponse.amount,
            currency: orderResponse.currency,
            name: 'Rupsa Clinic',
            description: `Appointment with ${doctorName}`,
            order_id: orderResponse.orderId,
            handler: async (response) => {
              try {
                // Verify payment
                const verificationResponse = await verifyPayment({
                  razorpayOrderId: response.razorpay_order_id,
                  razorpayPaymentId: response.razorpay_payment_id,
                  razorpaySignature: response.razorpay_signature,
                  bookingId: bookingId,
                  paymentMethod: paymentMethod,
                  paymentSubMethod: subOption
                });

                if (verificationResponse.success) {
                  onPaymentSuccess(verificationResponse.booking);
                } else {
                  onPaymentFailure('Payment verification failed');
                }
              } catch (error) {
                console.error('Payment verification error:', error);
                onPaymentFailure(error.message || 'Payment verification failed');
              }
            },
            prefill: {
              name: patientName,
              email: '',
              contact: ''
            },
            notes: {
              bookingId: bookingId,
              doctorName: doctorName,
              patientName: patientName,
              paymentMethod: paymentMethod,
              paymentSubMethod: subOption
            },
            theme: {
              color: '#2563eb'
            },
            modal: {
              ondismiss: () => {
                setLoading(false);
              }
            }
          };

          // Add payment method specific options
          if (paymentMethod === 'upi') {
            options.method = { upi: { flow: 'collect' } };
          } else if (paymentMethod === 'card') {
            options.method = { card: {} };
          } else if (paymentMethod === 'netbanking') {
            options.method = { netbanking: {} };
          } else if (paymentMethod === 'wallet') {
            options.method = { wallet: {} };
          } else if (paymentMethod === 'emi') {
            options.method = { emi: {} };
          }

          // Open Razorpay payment modal
          const razorpayInstance = new Razorpay(options);
          razorpayInstance.open();
        } catch (razorpayError) {
          console.error('Razorpay error:', razorpayError);
          setError('Payment gateway not available. Please try again later.');
          setLoading(false);
        }
      }

    } catch (error) {
      console.error('Payment error:', error);
      setError(error.message || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    setSelectedSubOption(null);
    setShowSubOptions(true);
  };

  const handleSubOptionSelect = (subOption) => {
    setSelectedSubOption(subOption);
  };

  const handleProceedToPayment = () => {
    if (!selectedMethod || !selectedSubOption) {
      setError('Please select both a payment method and a specific option');
      return;
    }
    handlePayment(selectedMethod.id, selectedSubOption.id);
  };

  const handleBackToMethods = () => {
    setShowSubOptions(false);
    setSelectedMethod(null);
    setSelectedSubOption(null);
    setError(null);
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Choose Payment Method</h2>
          <button onClick={onClose} className={styles.closeButton}>
            ×
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.summary}>
            <h3>Payment Summary</h3>
            <div className={styles.summaryItem}>
              <span>Doctor:</span>
              <span>{doctorName}</span>
            </div>
            <div className={styles.summaryItem}>
              <span>Patient:</span>
              <span>{patientName}</span>
            </div>
            <div className={styles.summaryItem}>
              <span>Amount:</span>
              <span className={styles.amount}>₹{amount}</span>
            </div>
          </div>

          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {!showSubOptions ? (
            <div className={styles.paymentMethods}>
              <h3>Select Payment Method</h3>
              <div className={styles.methodsGrid}>
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    className={`${styles.methodCard} ${selectedMethod?.id === method.id ? styles.selected : ''}`}
                    onClick={() => handleMethodSelect(method)}
                  >
                    <div className={styles.methodIcon} style={{ color: method.color }}>
                      {method.icon}
                    </div>
                    <div className={styles.methodInfo}>
                      <h4>{method.name}</h4>
                      <p>{method.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={styles.paymentMethods}>
              <div className={styles.breadcrumb}>
                <span 
                  className={styles.breadcrumbLink}
                  onClick={handleBackToMethods}
                >
                  ← Back to Payment Methods
                </span>
              </div>
              
              <h3>Select {selectedMethod.name} Option</h3>
              
              <div className={styles.methodsGrid}>
                {selectedMethod.subOptions.map((subOption) => (
                  <div
                    key={subOption.id}
                    className={`${styles.methodCard} ${selectedSubOption?.id === subOption.id ? styles.selected : ''}`}
                    onClick={() => handleSubOptionSelect(subOption)}
                  >
                    <div className={styles.methodIcon} style={{ color: subOption.color }}>
                      {subOption.icon}
                    </div>
                    <div className={styles.methodInfo}>
                      <h4>{subOption.name}</h4>
                      <p>Pay using {subOption.name}</p>
                    </div>
                    {selectedSubOption?.id === subOption.id && (
                      <div className={styles.checkmark}>✓</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={styles.footer}>
            {showSubOptions && selectedSubOption ? (
              <button
                onClick={handleProceedToPayment}
                disabled={loading}
                className={styles.proceedButton}
              >
                {loading ? 'Processing...' : `Pay ₹${amount} with ${selectedSubOption.name}`}
              </button>
            ) : (
              <button onClick={onClose} className={styles.cancelButton}>
                Cancel
              </button>
            )}
            <p className={styles.securityNote}>
              🔒 Your payment is secured by Razorpay
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentGateway; 